import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SolutionSection = () => {
  const [activeTab, setActiveTab] = useState('before');

  const beforeAfterData = {
    before: {
      title: "Manual Sequence Building",
      items: [
        { icon: "Search", text: "Search for leads in legacy databases", time: "60 min/day" },
        { icon: "FileText", text: "Export CSVs & clean bad data", time: "30 min/day" },
        { icon: "Upload", text: "Upload contacts to a sequence tool", time: "15 min/day" },
        { icon: "MessageSquare", text: "Write generic email templates", time: "45 min/day" },
        { icon: "Edit", text: "Manually personalize top accounts", time: "90 min/day" }
      ],
      totalTime: "4+ hours daily",
      results: "5-10 meetings / month"
    },
    after: {
      title: "AI-Powered SDR (LeadForge)",
      items: [
        { icon: "Database", text: "Syncs directly with our 275M+ DB", time: "0 min" },
        { icon: "Brain", text: "AI researches each website instantly", time: "0 min" },
        { icon: "Sparkles", text: "AI writes 1-to-1 personalized emails", time: "0 min" },
        { icon: "Send", text: "Auto-sends via your mailboxes", time: "0 min" },
        { icon: "Repeat", text: "Wakes up and repeats daily", time: "0 min" }
      ],
      totalTime: "5 minutes setup",
      results: "20-40 meetings / month"
    }
  };

  const benefits = [
    {
      icon: "TrendingUp",
      title: "3x More Booked Meetings",
      description: "1-to-1 personalized emails get significantly higher reply rates than generic templates."
    },
    {
      icon: "Clock",
      title: "Save 20+ Hours Weekly",
      description: "Never manually export a CSV or write a cold email sequence from scratch again."
    },
    {
      icon: "Database",
      title: "Full Integration",
      description: "Direct integration means you tap into the world's best B2B database instantly."
    },
    {
      icon: "Moon",
      title: "Works 24/7",
      description: "Your AI SDR pipeline runs constantly, filling your calendar with qualified demos."
    }
  ];

  return (
    <section id="solution" className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-headline-bold text-3xl md:text-5xl text-foreground mb-6">
            The Ultimate Outbound AI Workflow
          </h2>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto">
            Connect your data sources once, tell the AI who to target, and watch it generate hyper-personalized outreach campaigns that actually get replies.
          </p>
        </motion.div>

        {/* Before/After Comparison */}
        <div className="mb-20">
          <div className="flex justify-center mb-8">
            <div className="glassmorphism rounded-lg p-1 inline-flex">
              <Button
                variant={activeTab === 'before' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('before')}
                className="px-6 py-3"
              >
                Before
              </Button>
              <Button
                variant={activeTab === 'after' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('after')}
                className="px-6 py-3"
              >
                After
              </Button>
            </div>
          </div>

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: activeTab === 'before' ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="glassmorphism rounded-xl p-8"
          >
            <h3 className="font-headline-bold text-2xl text-foreground mb-8 text-center">
              {beforeAfterData[activeTab].title}
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                {beforeAfterData[activeTab].items.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-center space-x-4 p-4 rounded-lg bg-surface/50"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeTab === 'before' ? 'bg-error/10' : 'bg-primary/10'
                      }`}>
                      <Icon
                        name={item.icon}
                        size={20}
                        color={activeTab === 'before' ? 'var(--color-error)' : 'var(--color-primary)'}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-body-medium text-foreground">{item.text}</div>
                      <div className={`text-sm ${activeTab === 'before' ? 'text-error' : 'text-primary'
                        }`}>
                        {item.time}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col justify-center">
                <div className="text-center p-8 rounded-xl bg-surface/30">
                  <div className="text-4xl font-headline-black mb-4">
                    <span className={activeTab === 'before' ? 'text-error' : 'text-primary'}>
                      {beforeAfterData[activeTab].totalTime}
                    </span>
                  </div>
                  <div className="text-muted-foreground mb-6">Time Investment</div>

                  <div className="text-2xl font-headline-bold mb-2">
                    <span className={activeTab === 'before' ? 'text-error' : 'text-primary'}>
                      {beforeAfterData[activeTab].results}
                    </span>
                  </div>
                  <div className="text-muted-foreground">Results</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="glassmorphism rounded-xl p-6 text-center hover:scale-105 transition-transform duration-300"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={benefit.icon} size={32} color="var(--color-primary)" />
              </div>
              <h3 className="font-headline-bold text-lg text-foreground mb-3">
                {benefit.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;